import { Sparkle } from 'phosphor-react';
import React, { useMemo, useState } from 'react';
import { DillButton, DillCard, DillInput, DillSelect } from '../../../../general/Dill';
import { useAppDispatch } from '../../../../../redux/store';
import { rewriteReminderTone } from '../../../../../redux/services/supplierBuyersService';
import {
  AiNotificationMode,
  AiNotificationReminderType,
  AiNotificationSlot,
  AiNotificationTone,
} from '@dill/dill-shared';
import { openDillToast } from '../../../../../utils/helpers';
import { COLORS } from '../../../../../utils/colors';

// The channel the AI recommends this reminder type go out on - alongside cadence/tone, this is
// the third thing the AI decides. Not yet backed by real channel-routing logic (every reminder
// still actually sends as email) - the caller can pass a fixed value to preview what it'll look
// like once channel selection is real; left unset, it's randomized (weighted toward Email, the
// only one that's real) so a demo shows some channel variety without singling out a specific
// buyer id.
export type AiNotificationChannel = 'EMAIL' | 'CALL' | 'TEXT';

const CHANNEL_COPY: Record<AiNotificationChannel, { label: string; className: string }> = {
  EMAIL: { label: 'Email', className: 'bg-blue-50 text-blue-600' },
  CALL: { label: 'Call', className: 'bg-amber-50 text-amber-700' },
  TEXT: { label: 'Text', className: 'bg-emerald-50 text-emerald-700' },
};

const CHANNEL_WEIGHTS: [AiNotificationChannel, number][] = [
  ['EMAIL', 6],
  ['TEXT', 2],
];

const pickRandomChannel = (): AiNotificationChannel => {
  const total = CHANNEL_WEIGHTS.reduce((sum, [, weight]) => sum + weight, 0);
  let roll = Math.random() * total;
  for (const [channel, weight] of CHANNEL_WEIGHTS) {
    roll -= weight;
    if (roll <= 0) return channel;
  }
  return CHANNEL_WEIGHTS[CHANNEL_WEIGHTS.length - 1][0];
};

const ChannelPill = ({ channel }: { channel: AiNotificationChannel }) => {
  const { label, className } = CHANNEL_COPY[channel];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${className}`}>
      {label}
    </span>
  );
};

const MODE_OPTIONS: { label: string; value: AiNotificationMode }[] = [
  { label: 'Manual — you control cadence & wording', value: 'MANUAL' },
  { label: 'AI Recommends — Dill AI suggests, you review', value: 'AI_RECOMMENDS' },
  { label: 'Fully Agentic — Dill AI manages this for you', value: 'FULLY_AGENTIC' },
];

const TONE_OPTIONS: { label: string; value: AiNotificationTone }[] = [
  { label: '😐 Standard', value: 'STANDARD' },
  { label: '😊 Friendly', value: 'FRIENDLY' },
  { label: '😤 Firm', value: 'FIRM' },
  { label: '🧐 Formal', value: 'FORMAL' },
];

const TYPE_COPY: Record<AiNotificationReminderType, { title: string; description: string }> = {
  DUE_DATE: {
    title: 'AI — Due Date Reminders',
    description:
      'Let Dill AI set the cadence and wording of due-date payment reminder emails, based on best practices for this customer.',
  },
  CREDIT_HOLD: {
    title: 'AI — Credit Hold Reminders',
    description:
      'Let Dill AI set the cadence and wording of credit-hold reminder emails, based on best practices for this customer.',
  },
  DISCOUNT: {
    title: 'AI — Discount Deadline Reminders',
    description:
      'Let Dill AI set the cadence and wording of discount-deadline reminder emails, based on best practices for this customer.',
  },
};

const describeSlot = (reminderType: AiNotificationReminderType, dayOffset: number): string => {
  if (reminderType === 'DISCOUNT') {
    return `${dayOffset} day${dayOffset === 1 ? '' : 's'} before discount deadline`;
  }
  const noun = reminderType === 'CREDIT_HOLD' ? 'credit hold date' : 'due date';
  if (dayOffset > 0) return `${dayOffset} day${dayOffset === 1 ? '' : 's'} before ${noun}`;
  if (dayOffset === 0) return `On ${noun}`;
  return `${Math.abs(dayOffset)} day${Math.abs(dayOffset) === 1 ? '' : 's'} after ${noun}`;
};

// Mirrors the real default subject's day-difference phrase, built in
// sendEmailService.ts's sendInvoicePaymentReminderEmailService - so what's previewed here
// actually matches the email that goes out when a slot is left blank.
const dayDifferencePhrase = (dayOffset: number): string => {
  if (dayOffset === 0) return 'due today';
  if (dayOffset < 0) {
    const days = Math.abs(dayOffset);
    return `${days} day${days === 1 ? '' : 's'} past due`;
  }
  return `due in ${dayOffset} day${dayOffset === 1 ? '' : 's'}`;
};

// The real default body (no customNotes set) is just the "Details of your invoice are as
// below" subtitle above the invoice card - there's no separate "friendly reminder" sentence.
const DEFAULT_BODY_TEXT = 'Details of your invoice are as below.';

const defaultSlotCopy = (reminderType: AiNotificationReminderType, dayOffset: number) => {
  if (reminderType === 'DISCOUNT') {
    const daysText =
      dayOffset === 0 ? 'today' : dayOffset === 1 ? 'in 1 day' : `in ${dayOffset} days`;
    return {
      subject: `Your early pay discount on this invoice expires ${daysText}`,
      notes: DEFAULT_BODY_TEXT,
    };
  }
  if (reminderType === 'CREDIT_HOLD') {
    return {
      subject: `Approaching Credit Hold: Your invoice is ${dayDifferencePhrase(dayOffset)}`,
      notes:
        'To avoid any interruptions, please submit payment or respond with a payment update or remittance details.',
    };
  }
  return {
    subject: `Your invoice is ${dayDifferencePhrase(dayOffset)}`,
    notes: DEFAULT_BODY_TEXT,
  };
};

interface AgenticNotificationPreferencesSectionProps {
  reminderType: AiNotificationReminderType;
  supplierId: string;
  buyerId?: string;
  disabled?: boolean;
  mode: AiNotificationMode;
  onModeChange: (mode: AiNotificationMode) => void;
  /** Only this reminder type's slots - the parent filters the full slot list before passing it in. */
  slots: AiNotificationSlot[];
  onSlotsChange: (slots: AiNotificationSlot[]) => void;
  /** Only this reminder type's configured day-offsets. */
  offsets: number[];
  isGenerating: boolean;
  onGenerate: () => void;
  /** The channel the AI recommends this reminder type go out on. Left unset, it's randomized. */
  channel?: AiNotificationChannel;
  /**
   * Renders inline (a plain bordered block, compact header) instead of its own DillCard, for
   * placement directly under a reminder type's day-offset input inside a parent card - avoids a
   * card nested inside a card.
   */
  embedded?: boolean;
}

function AgenticNotificationPreferencesSection({
  reminderType,
  supplierId,
  buyerId,
  disabled,
  mode,
  onModeChange,
  slots,
  onSlotsChange,
  offsets,
  isGenerating,
  onGenerate,
  channel,
  embedded = false,
}: AgenticNotificationPreferencesSectionProps) {
  const dispatch = useAppDispatch();
  const [rewritingDayOffset, setRewritingDayOffset] = useState<number | null>(null);
  const copy = TYPE_COPY[reminderType];
  // Randomized per slot (once per day-offset) rather than once for the whole section - stable
  // for as long as this section stays on screen, but varies slot to slot instead of every due
  // date landing on the same channel. Only due-date reminders get channel variety - credit-hold
  // and discount reminders always show as Email since due dates are the only case the AI
  // channel pick is meant to cover.
  const randomChannelsByOffset = useMemo(() => {
    if (reminderType !== 'DUE_DATE') return {};
    const map: Partial<Record<number, AiNotificationChannel>> = {};
    offsets.forEach((dayOffset) => {
      map[dayOffset] = pickRandomChannel();
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderType, offsets.join(',')]);
  const resolveChannel = (dayOffset: number): AiNotificationChannel =>
    channel ?? randomChannelsByOffset[dayOffset] ?? 'EMAIL';

  const findSlot = (dayOffset: number) => slots.find((s) => s.dayOffset === dayOffset);

  const updateSlot = (dayOffset: number, patch: Partial<AiNotificationSlot>) => {
    const existing = findSlot(dayOffset);
    const base: AiNotificationSlot = existing ?? {
      reminderType,
      dayOffset,
      tone: 'STANDARD',
      subject: '',
      notes: '',
      isAiGenerated: false,
    };
    const updated: AiNotificationSlot = { ...base, ...patch };
    const rest = slots.filter((s) => s.dayOffset !== dayOffset);
    onSlotsChange([...rest, updated]);
  };

  const handleRewriteTone = async (dayOffset: number, tone: AiNotificationTone) => {
    if (!buyerId) return;
    const existing = findSlot(dayOffset);
    const fallback = defaultSlotCopy(reminderType, dayOffset);

    setRewritingDayOffset(dayOffset);
    try {
      const result = await dispatch(
        rewriteReminderTone({
          supplierId,
          buyerId,
          reminderType,
          tone,
          currentSubject: existing?.subject || fallback.subject,
          currentNotes: existing?.notes || fallback.notes,
        })
      );

      if (
        result.type !== 'supplierBuyers/rewriteReminderTone/fulfilled' ||
        !(result.payload as { subject?: string; notes?: string } | null)?.subject
      ) {
        openDillToast({ message: 'AI rewrite failed. Please try again.', type: 'ERROR' });
        return;
      }

      const { subject, notes } = result.payload as { subject: string; notes: string };
      updateSlot(dayOffset, { subject, notes, tone, isAiGenerated: true });
    } catch (error) {
      console.error('Failed to rewrite reminder tone:', error);
      openDillToast({ message: 'AI rewrite failed. Please try again.', type: 'ERROR' });
    } finally {
      setRewritingDayOffset(null);
    }
  };

  const handleSwitchToDefault = (dayOffset: number) => {
    updateSlot(dayOffset, {
      subject: '',
      notes: '',
      isAiGenerated: false,
    });
  };

  const content = (
    <>
      {embedded ? (
        <div className="flex items-center gap-2">
          <Sparkle size={14} color={COLORS.PRIMARY_500} weight="bold" />
          <p className="text-xs font-semibold text-gray-900">{copy.title}</p>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50">
            <Sparkle size={16} color={COLORS.PRIMARY_500} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">{copy.title}</h4>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{copy.description}</p>
          </div>
        </div>
      )}

      <div className="mt-4">
        <p className="text-xs font-medium text-gray-600 mb-2">Autonomy level</p>
        <DillSelect
          value={mode}
          onChange={(v) => onModeChange(v as AiNotificationMode)}
          options={MODE_OPTIONS}
          disabled={disabled}
        />
      </div>

      {mode !== 'MANUAL' && (
        <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {mode === 'FULLY_AGENTIC' ? 'Dill AI manages this plan' : 'AI-recommended plan'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {mode === 'FULLY_AGENTIC'
                ? 'Generates and applies a cadence + wording plan immediately.'
                : 'Generates a suggested plan below — review and save to apply it.'}
            </p>
          </div>
          <DillButton
            label={slots.length > 0 ? 'Regenerate' : 'Generate recommendation'}
            variant="secondary"
            icon={<Sparkle size={14} weight="bold" />}
            onClick={onGenerate}
            isLoading={isGenerating}
            isDisabled={disabled || !buyerId}
            height={34}
          />
        </div>
      )}

      {/* Fully Agentic already generates and applies the plan on its own - showing every slot
          for manual review/editing here would contradict the "hands-off" point of that mode. */}
      {mode === 'FULLY_AGENTIC' ? null : offsets.length === 0 ? (
        <p className="mt-4 text-xs text-gray-400">
          No reminders configured yet — add a day-offset above to see its email template here.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {/* Highest offset (furthest before the due date) first, counting down through the due
              date and into the after-due offsets - reads in the order a reminder plan actually
              plays out, not the order the offsets happen to have been added in. */}
          {[...offsets]
            .sort((a, b) => b - a)
            .map((dayOffset) => {
              const slot = findSlot(dayOffset);
              const fallback = defaultSlotCopy(reminderType, dayOffset);
              // Shown as real editable text rather than a placeholder hint - `||` (not `??`) so an
              // empty-string slot (e.g. after "Switch to default message") also falls back to it.
              // This is still just what's displayed: nothing is written to `aiSlots` until the
              // supplier actually edits the field, so an untouched slot still saves as blank and
              // the backend still computes its own real per-invoice default (actual $ amounts and
              // dates) at send time rather than this generic text.
              const subject = slot?.subject || fallback.subject;
              const notes = slot?.notes || fallback.notes;
              const tone = slot?.tone ?? 'STANDARD';
              const isRewriting = rewritingDayOffset === dayOffset;
              const slotState: 'default' | 'generating' | 'generated' = isRewriting
                ? 'generating'
                : slot?.isAiGenerated
                ? 'generated'
                : 'default';

              return (
                <div
                  key={dayOffset}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {describeSlot(reminderType, dayOffset)}
                    </span>
                    <ChannelPill channel={resolveChannel(dayOffset)} />
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mb-2">
                    {slotState === 'default' && (
                      <button
                        type="button"
                        onClick={() => handleRewriteTone(dayOffset, tone)}
                        disabled={disabled || !buyerId}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer">
                        <Sparkle size={12} weight="bold" />
                        Rewrite message
                      </button>
                    )}

                    {slotState === 'generating' && (
                      <div className="flex items-center gap-2">
                        <Sparkle
                          size={12}
                          weight="bold"
                          className="text-purple-500 animate-pulse"
                        />
                        <span className="text-xs font-medium text-purple-700">
                          Generating message based on customer history and invoice status…
                        </span>
                      </div>
                    )}

                    {slotState === 'generated' && (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Sparkle size={12} weight="bold" className="text-purple-600 shrink-0" />
                          <span className="text-xs font-semibold text-purple-700 shrink-0">
                            Generated
                          </span>
                          <div className="w-32">
                            <DillSelect
                              value={tone}
                              onChange={(v) =>
                                handleRewriteTone(dayOffset, v as AiNotificationTone)
                              }
                              options={TONE_OPTIONS}
                              disabled={disabled}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleRewriteTone(dayOffset, tone)}
                            disabled={disabled}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 bg-transparent border-none cursor-pointer">
                            Rewrite
                          </button>
                          <DillButton
                            label="Switch to default message"
                            variant="secondary"
                            onClick={() => handleSwitchToDefault(dayOffset)}
                            isDisabled={disabled}
                            height={30}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {slotState === 'default' && (
                    <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                      Preview of default wording
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    <DillInput
                      value={slotState === 'generating' ? '' : subject}
                      onChange={(v) => updateSlot(dayOffset, { subject: v })}
                      placeholder={
                        slotState === 'generating' ? 'Writing subject…' : fallback.subject
                      }
                      disabled={disabled || slotState === 'generating'}
                    />
                    <textarea
                      value={slotState === 'generating' ? '' : notes}
                      onChange={(e) => updateSlot(dayOffset, { notes: e.target.value })}
                      placeholder={
                        slotState === 'generating'
                          ? 'Writing message based on customer history and invoice status…'
                          : fallback.notes
                      }
                      disabled={disabled || slotState === 'generating'}
                      rows={4}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 disabled:bg-gray-50 disabled:text-gray-400 resize-none"
                    />
                  </div>

                  {slotState === 'default' && (
                    <p className="mt-1.5 text-[11px] text-gray-400">
                      Left blank, this reminder is sent using Dill's standard wording, with the
                      actual invoice amounts and dates filled in at send time.
                    </p>
                  )}
                  {slotState === 'generated' && (
                    <p className="mt-1.5 text-[11px] text-gray-400">
                      Review before you send. Important information about how we use generative AI.
                    </p>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </>
  );

  return embedded ? (
    <div className="mt-4 rounded-lg border border-purple-100 bg-white px-4 py-3">{content}</div>
  ) : (
    <DillCard className="bg-white border-purple-100">{content}</DillCard>
  );
}

export default AgenticNotificationPreferencesSection;
